/**
 * Context-aware suggestion templates for the copilot and widget
 * Generates relevant questions based on the context provided by the host application
 */

const APP_INSTANCE = import.meta.env.VITE_APP_INSTANCE ?? "GS";

export interface CopilotContextData {
    // Equipment context
    equipmentId?: string;
    equipmentType?: string;
    location?: string;

    // Alert/Deviation context
    alertDescription?: string;
    deviationId?: string;
    deviationTitle?: string;
    deviationCategory?: string;
    severity?: string;

    // Train/Unit context
    trainName?: string;
    productFamily?: string;
    equipmentName?: string;

    // Date context
    createdDate?: string;
    lastOccurred?: string;

    // Additional metadata
    metadata?: Record<string, string>;
}

// Default suggestions when no context is provided
export let DEFAULT_SUGGESTIONS = [];
if (APP_INSTANCE === "GS") {
    DEFAULT_SUGGESTIONS = [
        "Shaft Speed Spread probe sensors are found to be out of expected range, can you provide the symptom classes and step wise recommendations?",
        "What are the most common problems associated to TCP rotors?",
        "Provide the conclusion and recommendations to the customer MODEC Carioca for the commissioning activity on SGT-A35.",
        "What was the reason for the shutdown in HI6074/03?",
    ];
} else if (APP_INSTANCE === "GT") {
    DEFAULT_SUGGESTIONS = [
        "What failure modes are associated with the Surge Arrestors installed at Greenlink?",
        "What kind of pole convertors are installed at CELTIC?",
    ];
} else if (APP_INSTANCE === "STG") {
    DEFAULT_SUGGESTIONS = [
        "Have you ever detected corrosion patterns seen on turbine B002534?",
        "What are the root causes for control valve severity rating?",
    ];
}

// Default suggestions for external users
export const EXTERNAL_USER_SUGGESTIONS = [
    "What can you do for me?",
    "What are the last notification reports available to me?",
    "Give me findings and recommendations from the last report",
];

// Alert/troubleshooting focused suggestions
const ALERT_SUGGESTIONS = [
    "What are the possible root causes for this issue?",
    "What are the step-by-step troubleshooting procedures?",
    "Are there any known solutions or workarounds?",
    "What safety precautions should be taken?",
];

// Equipment focused suggestions
const EQUIPMENT_SUGGESTIONS = [
    "What are the common issues with this equipment?",
    "What is the recommended maintenance schedule?",
    "Show me the technical specifications",
    "What spare parts are typically needed?",
];

/**
 * Generate deviation-specific question templates
 * Reused from the widget's template generator
 */
export function generateDeviationTemplates(context: CopilotContextData): string[] {
    const templates: string[] = [];

    const trainName = context.trainName || context.metadata?.["train-name"];
    const title =
        context.deviationTitle || context.alertDescription || context.metadata?.["deviation-title"];
    const productFamily = context.productFamily || context.metadata?.["product-family"];
    const equipmentName =
        context.equipmentName || context.equipmentId || context.metadata?.["equipment-name"];
    const deviationCategory = context.deviationCategory || context.metadata?.["deviation-category"];
    const severity = context.severity || context.metadata?.severity;
    const createdDate = context.createdDate || context.metadata?.["created-date"];
    const lastOccurred = context.lastOccurred || context.metadata?.["last-occurred"];

    // Template 1: Simple and Direct - Check for similar cases
    if (title && trainName) {
        let simple = `Has deviation "${title}" happened before for unit ${trainName}`;
        if (productFamily) {
            simple += ` (${productFamily})`;
        }
        simple += "?";
        templates.push(simple);
    }

    // Template 2: Detailed Troubleshooting Request
    if (title && trainName) {
        let detailed = `Analyzing deviation "${title}" on ${trainName}`;
        if (equipmentName) {
            detailed += `, equipment ${equipmentName}`;
        }
        if (productFamily) {
            detailed += ` (${productFamily})`;
        }
        detailed += ". Please provide:\n";
        detailed += "1. Common root causes\n";
        detailed += "2. Recommended troubleshooting steps\n";
        detailed += "3. Similar historical cases\n";
        detailed += "4. Preventive measures";
        templates.push(detailed);
    }

    // Template 3: Contextual with Case Information
    if (title && trainName && equipmentName) {
        const severityText = severity ? `, Severity ${severity}` : "";
        const dateInfo: string[] = [];

        if (createdDate) {
            try {
                const date = new Date(createdDate);
                dateInfo.push(`Created: ${date.toLocaleDateString()}`);
            } catch {
                // Ignore date parsing errors
            }
        }
        if (lastOccurred) {
            try {
                const date = new Date(lastOccurred);
                dateInfo.push(`Last occurred: ${date.toLocaleDateString()}`);
            } catch {
                // Ignore date parsing errors
            }
        }

        let contextual = `Equipment ${equipmentName} on unit ${trainName} is showing "${title}". This is a ${deviationCategory || "Monitoring"} category${severityText} deviation`;
        if (dateInfo.length > 0) {
            contextual += `. ${dateInfo.join(", ")}`;
        }
        contextual +=
            ". Please provide troubleshooting guidance and any relevant historical context.";
        templates.push(contextual);
    }

    return templates;
}

/**
 * Generate equipment-specific question templates
 */
export function generateEquipmentTemplates(context: CopilotContextData): string[] {
    const templates: string[] = [];
    const equipmentId = context.equipmentId || context.equipmentName;
    const equipmentType = context.equipmentType || context.productFamily;

    if (equipmentId) {
        templates.push(`What are the common issues with ${equipmentId}?`);
        templates.push(`What is the recommended maintenance schedule for ${equipmentId}?`);

        if (equipmentType) {
            templates.push(`Show me the technical specifications for ${equipmentType}`);
            templates.push(`What spare parts are typically needed for ${equipmentType}?`);
        } else {
            templates.push(`Show me the technical specifications for ${equipmentId}`);
            templates.push(`What spare parts are typically needed for ${equipmentId}?`);
        }
    }

    return templates.length > 0 ? templates : EQUIPMENT_SUGGESTIONS;
}

/**
 * Generate alert-specific question templates
 */
export function generateAlertTemplates(context: CopilotContextData): string[] {
    const templates: string[] = [];
    const alert = context.alertDescription || context.deviationTitle;
    const equipmentId = context.equipmentId || context.equipmentName;

    if (alert) {
        templates.push(`What are the possible root causes for: "${alert}"?`);
        templates.push(`What are the step-by-step troubleshooting procedures for this issue?`);

        if (equipmentId) {
            templates.push(`Has this issue occurred before on ${equipmentId}?`);
            templates.push(`What are the safety precautions for ${equipmentId} with this alert?`);
        } else {
            templates.push(`Are there any known solutions or workarounds?`);
            templates.push(`What safety precautions should be taken?`);
        }
    }

    return templates.length > 0 ? templates : ALERT_SUGGESTIONS;
}

/**
 * Main function to generate context-aware suggestions
 * Intelligently selects the best template type based on available context
 */
export function getContextualSuggestions(
    context?: CopilotContextData,
    isExternalUser?: boolean,
): string[] {
    if (!context) {
        return isExternalUser ? EXTERNAL_USER_SUGGESTIONS : DEFAULT_SUGGESTIONS;
    }

    // Check if this is a deviation context (has deviation-specific fields)
    const hasDeviationContext = !!(
        context.deviationId ||
        context.deviationTitle ||
        context.trainName ||
        context.metadata?.["deviation-id"] ||
        context.metadata?.["deviation-title"] ||
        context.metadata?.["train-name"]
    );

    if (hasDeviationContext) {
        const deviationTemplates = generateDeviationTemplates(context);
        if (deviationTemplates.length > 0) {
            console.info(
                `[Context Templates] Generated ${deviationTemplates.length} deviation templates`,
            );
            return deviationTemplates;
        }
    }

    // Check if this is an alert context
    const hasAlertContext = !!(context.alertDescription || context.deviationTitle);
    if (hasAlertContext) {
        const alertTemplates = generateAlertTemplates(context);
        console.info(`[Context Templates] Generated ${alertTemplates.length} alert templates`);
        return alertTemplates;
    }

    // Check if this is equipment context
    const hasEquipmentContext = !!(
        context.equipmentId ||
        context.equipmentName ||
        context.equipmentType
    );
    if (hasEquipmentContext) {
        const equipmentTemplates = generateEquipmentTemplates(context);
        console.info(
            `[Context Templates] Generated ${equipmentTemplates.length} equipment templates`,
        );
        return equipmentTemplates;
    }

    // Default suggestions
    return isExternalUser ? EXTERNAL_USER_SUGGESTIONS : DEFAULT_SUGGESTIONS;
}

/**
 * Convert raw metadata record to CopilotContextData
 * Useful when receiving context from postMessage or widget
 */
export function parseContextFromMetadata(metadata: Record<string, string>): CopilotContextData {
    return {
        equipmentId: metadata["equipment-id"] || metadata.equipmentId,
        equipmentType: metadata["equipment-type"] || metadata.equipmentType,
        equipmentName: metadata["equipment-name"] || metadata.equipmentName,
        location: metadata.location,
        alertDescription: metadata["alert-description"] || metadata.alertDescription,
        deviationId: metadata["deviation-id"] || metadata.deviationId,
        deviationTitle: metadata["deviation-title"] || metadata.deviationTitle,
        deviationCategory: metadata["deviation-category"] || metadata.deviationCategory,
        severity: metadata.severity,
        trainName: metadata["train-name"] || metadata.trainName,
        productFamily: metadata["product-family"] || metadata.productFamily,
        createdDate: metadata["created-date"] || metadata.createdDate,
        lastOccurred: metadata["last-occurred"] || metadata.lastOccurred,
        metadata,
    };
}
